<template>
  <div>
    <dropdown-menu-button
      :iconColor="iconColor"
      :disabled="disabled"
      :direction="direction"
      :loading="loading"
    >
      <v-list>
        <v-list-item
          v-for="(control, idx) in controlsList"
          :key="idx"
          color="light"
          @click="groupAction(control)"
        >
          <v-list-item-title>
            {{ control.text }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </dropdown-menu-button>

    <component
      :is="modalComponent"
      :group="group"
      @input="showRemoveConfirmation = false"
      @groupRemoved="$emit('groupRemoved')"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';

export default {
  name: 'GroupControls',
  props: {
    iconColor: {
      type: String,
      default: 'appMain',
    },
    disabled: Boolean,
    group: {
      type: Object,
      default: () => ({}),
    },
    direction: String,
  },
  data() {
    return {
      loading: false,
      showRemoveConfirmation: false,
      groupControls: [
        {
          text: 'Start Outreach',
          action: 'startOutreach',
          component: '',
          check: () => this.isVectorRecruiting,
        },
        {
          text: 'Text Group',
          action: 'showModal',
          component: 'ConversationsChat',
          check: () =>
            this.hasPermissionToSendMessages && !this.isVectorRecruiting,
        },
        {
          text: 'Email Group',
          action: 'showModal',
          component: 'ConversationsEmail',
          check: () =>
            this.hasPermissionToSendMessages && !this.isVectorRecruiting,
        },
        {
          text: 'Invite Contacts',
          action: 'inviteGroupContacts',
          check: () => this.isContactInviteEnabled && !this.isVectorRecruiting,
        },
        {
          text: 'Add Contacts to Campaign',
          action: 'showModal',
          component: 'GroupAddToCampaignDialog',
          check: () =>
            this.isFeatureEnabled('campaigns') && !this.isVectorRecruiting,
        },
        {
          text: 'Export Contacts',
          action: 'showModal',
          component: 'ContactsExport',
          check: () => !this.isVectorRecruiting,
        },
        {
          text: 'Make a copy',
          action: 'copyGroup',
          component: '',
          check: () => !this.isVectorRecruiting,
        },
        {
          text: 'Copy Filters to Clipboard',
          action: 'copyFiltersToClipboard',
          component: '',
          check: () => !this.isVectorRecruiting,
        },
        {
          text: 'Advanced Settings',
          action: 'showModal',
          component: 'GroupSettingsDialog',
          check: () => this.isAdmin && !this.isVectorRecruiting,
        },
        {
          text: 'Set Folder',
          action: 'editFolder',
          component: {
            folderId: '',
            group: this.group,
          },
        },
        {
          text: 'Remove from folder',
          action: 'removeFromFolder',
          component: '',
          check: () => !!this.group?.folder_id,
        },
        {
          text: 'Create Automation',
          action: 'createAutomation',
          component: '',
          check: () =>
            this.isFeatureEnabled('automations') && !this.isVectorRecruiting,
        },
        {
          text: 'Delete Group',
          action: 'showDeleteConfirmation',
          component: '',
          check: () => {
            const { id, role } = this.currentUser || {};
            return this.group?.user_id == id || role === 'admin';
          },
        },
      ],
    };
  },
  computed: {
    ...mapGetters('users', [
      'currentUser',
      'isAdmin',
      'isContactInviteEnabled',
    ]),
    controlsList() {
      return this.groupControls.filter(({ check }) => {
        return !check || check();
      });
    },

    modalComponent() {
      if (this.showRemoveConfirmation) {
        return () => import('./GroupRemoveConfirmation');
      }
      return '';
    },

    hasPermissionToSendMessages() {
      return (
        this.isFeatureEnabled('conversations') &&
        (this.isAdmin || this.hasPermission('group_messages'))
      );
    },
  },
  methods: {
    ...mapActions([
      'deleteGroup',
      'updateGroup',
      'createGroup',
      'inviteContacts',
    ]),
    ...mapActions('groupsFolders', {
      loadFolder: 'loadOne',
    }),

    groupAction({ action, component }) {
      if (['showModal', 'editFolder'].includes(action)) {
        return this.$emit(action, component);
      }
      this[action]();
    },

    startOutreach() {
      this.$router.push({
        name: 'group-outreach',
        params: {
          id: this.group.id,
        },
      });
    },

    createAutomation() {
      this.$router.push({
        name: 'automation-create',
        query: {
          group: this.group.id,
        },
      });
    },

    showDeleteConfirmation() {
      this.showRemoveConfirmation = true;
    },

    async removeFromFolder() {
      const folderId = this.group.folder_id;
      await this.updateGroup(
        Object.assign(clone(this.group), { folder_id: '' })
      );
      this.loadFolder({ path: folderId });
    },

    async copyGroup() {
      const groupParams = ['filters', 'order', 'folder_id'];

      let groupCopy = {
        name: `COPY - ${this.group.name}`,
        ...pick(cloneDeep(this.group), groupParams),
      };

      if (this.group.type === 'tag') {
        groupCopy.type = 'tag';
        groupCopy.tag = this.group.tag;
      }

      this.loading = true;

      try {
        const group = await this.createGroup(groupCopy);
        this.openGroup(group?.id);
      } finally {
        this.loading = false;
      }
    },

    copyFiltersToClipboard() {
      if (this.group.filters) {
        navigator.clipboard.writeText(
          JSON.stringify(this.group.filters, null, 2)
        );
      }
    },

    openGroup(id = '') {
      if (!id) return;
      this.$router.push({
        name: 'group-edit',
        params: { id },
      });
    },

    async inviteGroupContacts() {
      const groupId = this.group.id;
      if (!groupId) return;

      try {
        this.loading = true;
        await this.inviteContacts({
          group_id: groupId,
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
